
import React, { useState, useRef, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Send, 
  Loader2, 
  Wand2, 
  FileText, 
  Sparkles, 
  X,
  Copy,
  Edit3
} from 'lucide-react';
import { cn } from "@/lib/utils";

export type ChatMode = 'free' | 'template' | 'enhance';

interface Template {
  id: string;
  title: string;
  description: string;
  content: string;
  category: string;
  type: 'saved' | 'bookmark' | 'popular' | 'product' | 'collection';
  custom_title?: string;
  notes?: string;
}

interface EnhancedChatInputProps {
  input: string;
  setInput: (value: string) => void;
  onSend: () => void;
  isGenerating: boolean;
  onOpenTemplatePicker: () => void;
  selectedTemplate?: Template | null;
  onClearTemplate?: () => void;
  mode: ChatMode;
  onModeChange: (mode: ChatMode) => void;
}

const EnhancedChatInput: React.FC<EnhancedChatInputProps> = ({
  input,
  setInput,
  onSend,
  isGenerating,
  onOpenTemplatePicker,
  selectedTemplate,
  onClearTemplate,
  mode,
  onModeChange
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [showTemplateReference, setShowTemplateReference] = useState(true);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 200)}px`;
    }
  }, [input]);

  const handleSubmit = () => {
    if (input.trim() && !isGenerating) {
      onSend();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleCopyTemplate = () => {
    if (selectedTemplate) {
      navigator.clipboard.writeText(selectedTemplate.content);
    }
  };

  const getPlaceholder = () => {
    switch (mode) {
      case 'template':
        return selectedTemplate 
          ? "Type your message here..."
          : "Select a template to get started...";
      case 'enhance':
        return "Enter your basic idea and I'll transform it into a professional, detailed prompt...";
      default:
        return "Type your message here...";
    }
  };

  const getButtonText = () => {
    if (isGenerating) {
      return mode === 'enhance' ? 'Enhancing...' : 'Sending...';
    }
    return mode === 'enhance' ? 'Enhance' : 'Send';
  };

  return (
    <div className="border-t bg-background">
      {/* Template Mode Header */}
      {mode === 'template' && selectedTemplate && (
        <div className="p-4 border-b bg-purple-50 dark:bg-purple-900/20">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="bg-purple-100 text-purple-700 border-purple-200">
                <FileText className="h-3 w-3 mr-1" />
                Template Mode
              </Badge>
              <span className="text-sm font-medium text-purple-700 dark:text-purple-300">
                {selectedTemplate.custom_title || selectedTemplate.title}
              </span>
              <Badge variant="secondary" className="text-xs">
                {selectedTemplate.category}
              </Badge>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClearTemplate}
              className="h-7 text-xs text-purple-600 hover:text-purple-700"
            >
              Switch to Free Chat
            </Button>
          </div>
          
          {showTemplateReference && (
            <Card className="mb-3">
              <CardContent className="p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-gray-600">Template Reference</span>
                  <div className="flex gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleCopyTemplate}
                      className="h-6 text-xs px-2"
                    >
                      <Copy className="h-3 w-3 mr-1" />
                      Copy
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={onOpenTemplatePicker}
                      className="h-6 text-xs px-2"
                    >
                      <Edit3 className="h-3 w-3 mr-1" />
                      Edit in Canvas
                    </Button>
                  </div>
                </div>
                <div className="text-xs text-gray-500 bg-gray-50 dark:bg-gray-800 p-2 rounded max-h-20 overflow-y-auto">
                  {selectedTemplate.content}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Enhance Mode Header */}
      {mode === 'enhance' && (
        <div className="p-4 border-b bg-green-50 dark:bg-green-900/20">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="bg-green-100 text-green-700 border-green-200">
                <Wand2 className="h-3 w-3 mr-1" />
                Enhance Mode
              </Badge>
              <span className="text-sm font-medium text-green-700 dark:text-green-300">
                Prompt Builder
              </span>
              <Badge variant="secondary" className="text-xs">
                AI Enhanced
              </Badge>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onModeChange('free')}
              className="h-7 text-xs text-green-600 hover:text-green-700"
            >
              Switch to Free Chat
            </Button>
          </div>
        </div>
      )}

      {/* Chat Input Section */}
      <div className="p-4">
        <div className="flex gap-3">
          {/* Mode Selection Buttons */}
          <div className="flex flex-col gap-2">
            <Button
              variant={mode === 'template' ? 'default' : 'outline'}
              size="sm"
              onClick={onOpenTemplatePicker}
              className={cn(
                "h-14 px-4 flex flex-col items-center gap-1 min-w-[90px]",
                mode === 'template' && "bg-purple-600 hover:bg-purple-700 text-white border-purple-600"
              )}
            >
              <FileText className="h-4 w-4" />
              <span className="text-xs font-medium">Template</span>
            </Button>

            <Button
              variant={mode === 'enhance' ? 'default' : 'outline'}
              size="sm"
              onClick={() => onModeChange(mode === 'enhance' ? 'free' : 'enhance')}
              className={cn(
                "h-14 px-4 flex flex-col items-center gap-1 min-w-[90px]",
                mode === 'enhance' && "bg-green-600 hover:bg-green-700 text-white border-green-600"
              )}
            >
              <Wand2 className="h-4 w-4" />
              <span className="text-xs font-medium">Enhance</span>
            </Button>
          </div>

          {/* Text Input - Now without right padding for button */}
          <div className="flex-1">
            <Textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={getPlaceholder()}
              disabled={isGenerating}
              className="resize-none text-base min-h-[60px] max-h-[200px] shadow-sm"
              rows={3}
            />
          </div>

          {/* Send Button - Now completely outside the textarea */}
          <div className="flex flex-col justify-end">
            <Button 
              onClick={handleSubmit}
              disabled={!input.trim() || isGenerating}
              size="sm"
              className={cn(
                "h-14 px-4 flex flex-col items-center gap-1 min-w-[90px] shadow-sm",
                mode === 'enhance' && "bg-green-600 hover:bg-green-700",
                mode === 'template' && "bg-purple-600 hover:bg-purple-700"
              )}
            >
              {isGenerating ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="text-xs">
                    {mode === 'enhance' ? 'Enhancing...' : 'Sending...'}
                  </span>
                </>
              ) : mode === 'enhance' ? (
                <>
                  <Wand2 className="h-4 w-4" />
                  <span className="text-xs font-medium">Enhance</span>
                </>
              ) : (
                <>
                  <Send className="h-4 w-4" />
                  <span className="text-xs font-medium">Send</span>
                </>
              )}
            </Button>
          </div>
        </div>
        
        {/* Helper Text */}
        <div className="flex items-center justify-between mt-3">
          <p className="text-xs text-gray-500">
            {mode === 'enhance' 
              ? `Press Enter or click "Enhance" to transform your idea into a professional prompt`
              : mode === 'template'
              ? 'Get AI responses guided by pre-built templates'
              : 'Press Enter to send, Shift+Enter for new line • Natural AI conversation'
            }
          </p>
          <div className="text-xs text-gray-400">
            {input.length} characters
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnhancedChatInput;
